import type { TypingLesson, TypingDrill } from '../../lib/types';

interface TypingDayPlan {
  slug: string;
  title: string;
  titleFa: string;
  text: string;
  focus: string;
  category: TypingLesson['category'];
}

interface TypingWeekPlan {
  week: number;
  themeFa: string;
  difficulty: TypingLesson['difficulty'];
  days: TypingDayPlan[];
}

const buildInstruction = (week: number, themeFa: string, focus: string): string =>
  `هفته ${week} با موضوع «${themeFa}».\n` +
  `در این تمرین روی ${focus} تمرکز کنید.\n` +
  'چشم ها را تا حد امکان از صفحه کلید دور نگه دارید، ریتم یکنواخت داشته باشید و دقت را فدای سرعت نکنید.';

const typingWeekPlans: TypingWeekPlan[] = [
  {
    week: 1,
    themeFa: 'تسلط بر ردیف میانی',
    difficulty: 'beginner',
    days: [
      {
        slug: 'home-left-right',
        title: 'Home Row Flow 1',
        titleFa: 'جریان ردیف میانی ۱',
        text: 'asdf jkl; asdf jkl; asdf jkl; asdf jkl;',
        focus: 'قرارگیری صحیح انگشت ها روی ردیف میانی',
        category: 'homerow',
      },
      {
        slug: 'home-balance',
        title: 'Home Row Flow 2',
        titleFa: 'جریان ردیف میانی ۲',
        text: 'a s d f j k l ; a s d f j k l ;',
        focus: 'تعادل بین دست چپ و راست',
        category: 'homerow',
      },
      {
        slug: 'home-repeat',
        title: 'Home Row Rhythm',
        titleFa: 'ریتم ردیف میانی',
        text: 'asdf asdf jkl; jkl; asdf jkl; asdf jkl;',
        focus: 'حفظ ریتم یکنواخت در تایپ پایه',
        category: 'homerow',
      },
      {
        slug: 'home-word-bridge',
        title: 'Bridge to Words',
        titleFa: 'پل به کلمات کوتاه',
        text: 'sad fall ask all flask; lad asks; dad falls;',
        focus: 'تبدیل الگوهای کلیدی به کلمه',
        category: 'homerow',
      },
      {
        slug: 'home-accuracy',
        title: 'Accuracy Drill',
        titleFa: 'تمرین دقت',
        text: 'fads; flask; asks; all; sad; jall; lask; asdf;',
        focus: 'کاهش خطای تایپی با سرعت کم',
        category: 'homerow',
      },
      {
        slug: 'home-short-line',
        title: 'Short Line Practice',
        titleFa: 'تمرین خط کوتاه',
        text: 'A fast lad asks. A tall lass falls. A small task.',
        focus: 'استفاده از Shift و نقطه در جملات کوتاه',
        category: 'sentences',
      },
      {
        slug: 'home-week-review',
        title: 'Week 1 Review',
        titleFa: 'مرور هفته ۱',
        text: 'asdf jkl; A fast lad asks. sad fall ask all.',
        focus: 'مرور یکپارچه مهارت های ردیف میانی',
        category: 'homerow',
      },
    ],
  },
  {
    week: 2,
    themeFa: 'افزودن حروف پرکاربرد',
    difficulty: 'beginner',
    days: [
      {
        slug: 'common-words-1',
        title: 'Common Words Set 1',
        titleFa: 'کلمات پرکاربرد ۱',
        text: 'the and this that there these those then',
        focus: 'کلمات کوتاه پرتکرار در مکالمه',
        category: 'common-words',
      },
      {
        slug: 'common-words-2',
        title: 'Common Words Set 2',
        titleFa: 'کلمات پرکاربرد ۲',
        text: 'with from have been into over under after',
        focus: 'حرکت نرم بین حروف بالا و میانی',
        category: 'common-words',
      },
      {
        slug: 'common-phrases-1',
        title: 'Useful Phrases 1',
        titleFa: 'عبارت های کاربردی ۱',
        text: 'thank you very much. how are you today. see you soon.',
        focus: 'ترکیب کلمات پرتکرار در عبارت های واقعی',
        category: 'sentences',
      },
      {
        slug: 'common-phrases-2',
        title: 'Useful Phrases 2',
        titleFa: 'عبارت های کاربردی ۲',
        text: 'please call me later. i will send the file. we can start now.',
        focus: 'حفظ یکنواختی فاصله و نشانه گذاری',
        category: 'sentences',
      },
      {
        slug: 'common-question',
        title: 'Question Patterns',
        titleFa: 'الگوهای پرسشی',
        text: 'where are you now. what time is class. why are they late.',
        focus: 'تایپ سریع سوال های روزمره',
        category: 'sentences',
      },
      {
        slug: 'common-capitalization',
        title: 'Capitalization Basics',
        titleFa: 'حروف بزرگ در جمله',
        text: 'The class starts early. My son reads daily. We practice typing.',
        focus: 'شروع جمله با حرف بزرگ',
        category: 'sentences',
      },
      {
        slug: 'common-week-review',
        title: 'Week 2 Review',
        titleFa: 'مرور هفته ۲',
        text: 'thank you very much. we can start now. the class starts early.',
        focus: 'مرور واژگان و جمله های پایه',
        category: 'common-words',
      },
    ],
  },
  {
    week: 3,
    themeFa: 'ردیف پایینی و نشانه گذاری ساده',
    difficulty: 'beginner',
    days: [
      {
        slug: 'bottom-row-1',
        title: 'Bottom Row Entry 1',
        titleFa: 'ورود به ردیف پایینی ۱',
        text: 'z x c v b n m, z x c v b n m,',
        focus: 'حرکت کنترل شده به ردیف پایینی',
        category: 'homerow',
      },
      {
        slug: 'bottom-row-2',
        title: 'Bottom Row Entry 2',
        titleFa: 'ورود به ردیف پایینی ۲',
        text: 'mix, box, calm, zoom, civic, basic, cabin',
        focus: 'ترکیب ردیف پایینی با کلمات کوتاه',
        category: 'common-words',
      },
      {
        slug: 'punctuation-comma',
        title: 'Comma Control',
        titleFa: 'کنترل ویرگول',
        text: 'Today, we type slowly, carefully, and clearly.',
        focus: 'استفاده درست از ویرگول در جمله',
        category: 'sentences',
      },
      {
        slug: 'punctuation-period',
        title: 'Period and Rhythm',
        titleFa: 'نقطه و ریتم',
        text: 'My mom is calm. My son is active. We read and type daily.',
        focus: 'پایان بندی صحیح جمله با نقطه',
        category: 'sentences',
      },
      {
        slug: 'short-dialogue',
        title: 'Short Dialogue',
        titleFa: 'گفت و گوی کوتاه',
        text: 'Hi Mina, can we meet at noon. Yes, I can come at one.',
        focus: 'جابه جایی سریع بین کلمه ها در دیالوگ',
        category: 'sentences',
      },
      {
        slug: 'editing-flow',
        title: 'Edit and Re-type',
        titleFa: 'ویرایش و تایپ دوباره',
        text: 'type slow first. fix small errors. type the line again.',
        focus: 'فرآیند اصلاح خطا و تایپ مجدد',
        category: 'sentences',
      },
      {
        slug: 'bottom-week-review',
        title: 'Week 3 Review',
        titleFa: 'مرور هفته ۳',
        text: 'Today, we type slowly. Hi Mina, can we meet at noon.',
        focus: 'مرور ردیف پایینی و نشانه گذاری',
        category: 'sentences',
      },
    ],
  },
  {
    week: 4,
    themeFa: 'اعداد، زمان و متن های کاربردی',
    difficulty: 'intermediate',
    days: [
      {
        slug: 'numbers-1',
        title: 'Numbers and Dates',
        titleFa: 'اعداد و تاریخ',
        text: 'Today is 2026-02-22. Class starts at 08:30 and ends at 10:00.',
        focus: 'تایپ اعداد، دو نقطه و خط تیره',
        category: 'sentences',
      },
      {
        slug: 'numbers-2',
        title: 'Phone and Codes',
        titleFa: 'شماره و کد',
        text: 'Call me at 0912 345 6789. Use code A7B9 to login.',
        focus: 'دقت در ترکیب عدد و حروف',
        category: 'sentences',
      },
      {
        slug: 'time-schedule',
        title: 'Daily Schedule Lines',
        titleFa: 'خطوط برنامه روزانه',
        text: '09:00 check email. 11:30 call client. 14:00 submit report.',
        focus: 'تایپ خطوط زمان بندی کوتاه',
        category: 'sentences',
      },
      {
        slug: 'shopping-list',
        title: 'Shopping List Format',
        titleFa: 'فرمت لیست خرید',
        text: '1) rice 2kg, 2) milk 1L, 3) bread x2, 4) apples x6.',
        focus: 'حفظ نظم در لیست های شماره دار',
        category: 'sentences',
      },
      {
        slug: 'form-lines',
        title: 'Simple Form Lines',
        titleFa: 'خطوط فرم ساده',
        text: 'Name: Sara Ahmadi. Age: 42. City: Karaj. Job: Teacher.',
        focus: 'تایپ قالب های فرم و برچسب',
        category: 'sentences',
      },
      {
        slug: 'numbers-accuracy',
        title: 'Numeric Accuracy Drill',
        titleFa: 'تمرین دقت عددی',
        text: '485 390 274 661 903 118 502 744 620 999',
        focus: 'دقت بالا در توالی عددی',
        category: 'common-words',
      },
      {
        slug: 'numbers-week-review',
        title: 'Week 4 Review',
        titleFa: 'مرور هفته ۴',
        text: 'Class starts at 08:30. Name: Sara Ahmadi. Submit report at 14:00.',
        focus: 'مرور تایپ عدد، زمان و فرم',
        category: 'sentences',
      },
    ],
  },
  {
    week: 5,
    themeFa: 'جمله های بلندتر و پاراگراف کوتاه',
    difficulty: 'intermediate',
    days: [
      {
        slug: 'paragraph-1',
        title: 'Paragraph Starter 1',
        titleFa: 'شروع پاراگراف ۱',
        text: 'I wake up early, prepare breakfast, and plan my day before work.',
        focus: 'نگه داشتن ریتم در جمله طولانی',
        category: 'sentences',
      },
      {
        slug: 'paragraph-2',
        title: 'Paragraph Starter 2',
        titleFa: 'شروع پاراگراف ۲',
        text: 'After lunch, I review my notes, answer emails, and finish pending tasks.',
        focus: 'تقسیم جمله به بخش های ذهنی کوچک',
        category: 'sentences',
      },
      {
        slug: 'paragraph-3',
        title: 'Paragraph Starter 3',
        titleFa: 'شروع پاراگراف ۳',
        text: 'In the evening, our family talks together, reads a short story, and rests.',
        focus: 'ثبات سرعت در انتهای جمله',
        category: 'sentences',
      },
      {
        slug: 'email-lines',
        title: 'Email Sentence Flow',
        titleFa: 'جریان جمله در ایمیل',
        text: 'Hello team, I reviewed the document and added comments in section three.',
        focus: 'تایپ روان جمله های نیمه رسمی',
        category: 'sentences',
      },
      {
        slug: 'instructions-lines',
        title: 'Instruction Sentences',
        titleFa: 'جمله های دستوری',
        text: 'Please save the file, upload it to the shared folder, and notify the group.',
        focus: 'تایپ دستورهای چندمرحله ای',
        category: 'sentences',
      },
      {
        slug: 'summary-lines',
        title: 'Summary Sentences',
        titleFa: 'جمله های جمع بندی',
        text: 'The meeting was useful because we defined goals, deadlines, and next actions.',
        focus: 'دقت در جملات خبری طولانی',
        category: 'sentences',
      },
      {
        slug: 'paragraph-week-review',
        title: 'Week 5 Review',
        titleFa: 'مرور هفته ۵',
        text: 'I reviewed the document and added comments. Please upload the final file.',
        focus: 'مرور پاراگراف های کاربردی روزانه',
        category: 'sentences',
      },
    ],
  },
  {
    week: 6,
    themeFa: 'متن های کاری و نیمه رسمی',
    difficulty: 'intermediate',
    days: [
      {
        slug: 'work-mail-1',
        title: 'Work Email Practice 1',
        titleFa: 'ایمیل کاری ۱',
        text: 'Dear Mr. Rezaei, I am sending the updated report for your review.',
        focus: 'شروع رسمی ایمیل',
        category: 'sentences',
      },
      {
        slug: 'work-mail-2',
        title: 'Work Email Practice 2',
        titleFa: 'ایمیل کاری ۲',
        text: 'Please let me know if you need any additional data before tomorrow noon.',
        focus: 'جمله های درخواست مودبانه',
        category: 'sentences',
      },
      {
        slug: 'meeting-note-1',
        title: 'Meeting Notes 1',
        titleFa: 'یادداشت جلسه ۱',
        text: 'Action items: finalize budget, confirm schedule, and contact new vendors.',
        focus: 'تایپ سریع نکات کلیدی جلسه',
        category: 'sentences',
      },
      {
        slug: 'meeting-note-2',
        title: 'Meeting Notes 2',
        titleFa: 'یادداشت جلسه ۲',
        text: 'The team agreed to deliver phase one by Friday and report progress daily.',
        focus: 'حفظ دقت در جملات مدیریتی',
        category: 'sentences',
      },
      {
        slug: 'customer-reply',
        title: 'Customer Reply Lines',
        titleFa: 'پاسخ به مشتری',
        text: 'Thank you for your message. We received your request and will respond soon.',
        focus: 'سرعت مناسب در پاسخ های پشتیبانی',
        category: 'sentences',
      },
      {
        slug: 'report-line',
        title: 'Report Sentence Practice',
        titleFa: 'تمرین جمله گزارشی',
        text: 'Sales increased in week two; however, delivery delays affected satisfaction.',
        focus: 'کار با نقطه ویرگول و واژه های ربطی',
        category: 'ielts',
      },
      {
        slug: 'work-week-review',
        title: 'Week 6 Review',
        titleFa: 'مرور هفته ۶',
        text: 'Dear team, we finalized the budget and confirmed the delivery schedule.',
        focus: 'مرور نوشتار نیمه رسمی و کاری',
        category: 'sentences',
      },
    ],
  },
  {
    week: 7,
    themeFa: 'متن های آکادمیک و آیلتس مقدماتی',
    difficulty: 'advanced',
    days: [
      {
        slug: 'ielts-opinion-1',
        title: 'IELTS Opinion Line 1',
        titleFa: 'جمله دیدگاه آیلتس ۱',
        text: 'Many people believe that technology improves access to education worldwide.',
        focus: 'تایپ جمله دیدگاه با واژگان آکادمیک',
        category: 'ielts',
      },
      {
        slug: 'ielts-opinion-2',
        title: 'IELTS Opinion Line 2',
        titleFa: 'جمله دیدگاه آیلتس ۲',
        text: 'In my view, public transport should be expanded to reduce traffic congestion.',
        focus: 'ساخت جمله استدلالی با عبارت های پیوندی',
        category: 'ielts',
      },
      {
        slug: 'ielts-example',
        title: 'IELTS Example Sentence',
        titleFa: 'جمله مثال آیلتس',
        text: 'For example, online classes allow parents to study without leaving home.',
        focus: 'استفاده از عبارت For example در متن',
        category: 'ielts',
      },
      {
        slug: 'ielts-contrast',
        title: 'IELTS Contrast Sentence',
        titleFa: 'جمله تقابلی آیلتس',
        text: 'However, some learners still prefer face-to-face interaction in classrooms.',
        focus: 'تایپ جمله تقابلی با However',
        category: 'ielts',
      },
      {
        slug: 'ielts-result',
        title: 'IELTS Result Sentence',
        titleFa: 'جمله نتیجه آیلتس',
        text: 'As a result, communities need balanced policies for digital inclusion.',
        focus: 'نوشتن جمله نتیجه گیری روشن',
        category: 'ielts',
      },
      {
        slug: 'ielts-mini-paragraph',
        title: 'IELTS Mini Paragraph',
        titleFa: 'مینی پاراگراف آیلتس',
        text: 'Education quality rises when teachers receive training, resources, and ongoing support.',
        focus: 'نگهداری دقت در پاراگراف فشرده',
        category: 'ielts',
      },
      {
        slug: 'ielts-week-review',
        title: 'Week 7 Review',
        titleFa: 'مرور هفته ۷',
        text: 'Technology improves education; however, equal access remains a major challenge.',
        focus: 'مرور ساختار آکادمیک و پیوند دهنده ها',
        category: 'ielts',
      },
    ],
  },
  {
    week: 8,
    themeFa: 'روانی پیشرفته و جمع بندی نهایی',
    difficulty: 'advanced',
    days: [
      {
        slug: 'fluency-1',
        title: 'Advanced Fluency 1',
        titleFa: 'روانی پیشرفته ۱',
        text: 'If we practice consistently, we will type faster and communicate with confidence.',
        focus: 'حفظ روانی در جمله شرطی',
        category: 'ielts',
      },
      {
        slug: 'fluency-2',
        title: 'Advanced Fluency 2',
        titleFa: 'روانی پیشرفته ۲',
        text: 'Although mistakes happen, steady feedback and repetition lead to real progress.',
        focus: 'کنترل سرعت در جمله مرکب',
        category: 'ielts',
      },
      {
        slug: 'fluency-3',
        title: 'Advanced Fluency 3',
        titleFa: 'روانی پیشرفته ۳',
        text: 'During this course, I improved accuracy, increased speed, and reduced hesitation.',
        focus: 'پایداری ریتم در فهرست های طولانی',
        category: 'ielts',
      },
      {
        slug: 'fluency-4',
        title: 'Advanced Fluency 4',
        titleFa: 'روانی پیشرفته ۴',
        text: 'My next goal is to write clear emails quickly while maintaining professional tone.',
        focus: 'تایپ دقیق متن هدف گذاری شخصی',
        category: 'ielts',
      },
      {
        slug: 'fluency-5',
        title: 'Advanced Fluency 5',
        titleFa: 'روانی پیشرفته ۵',
        text: 'In conclusion, daily typing habits can transform both study performance and work output.',
        focus: 'پایان بندی رسمی و قوی',
        category: 'ielts',
      },
      {
        slug: 'fluency-6',
        title: 'Advanced Fluency 6',
        titleFa: 'روانی پیشرفته ۶',
        text: 'I will continue this routine, track my words per minute, and celebrate small wins.',
        focus: 'ترکیب آینده، عدد و واژه انگیزشی',
        category: 'ielts',
      },
      {
        slug: 'fluency-final-review',
        title: 'Final Review Session',
        titleFa: 'جلسه مرور نهایی',
        text: 'Consistent practice built my speed, accuracy, and confidence across eight weeks.',
        focus: 'جمع بندی نهایی با تاکید بر دقت و روانی',
        category: 'ielts',
      },
    ],
  },
];

export const typingLessons: TypingLesson[] = typingWeekPlans.flatMap((weekPlan) =>
  weekPlan.days.map((day, index) => {
    const weekId = String(weekPlan.week).padStart(2, '0');
    const dayId = String(index + 1).padStart(2, '0');

    return {
      id: `typing-w${weekId}-d${dayId}-${day.slug}`,
      title: `Week ${weekPlan.week}: ${day.title}`,
      titleFa: `هفته ${weekPlan.week}: ${day.titleFa}`,
      instruction: buildInstruction(weekPlan.week, weekPlan.themeFa, day.focus),
      text: day.text,
      difficulty: weekPlan.difficulty,
      category: day.category,
    };
  }),
);

const drillTextFromLesson = (text: string): string => {
  const clean = text.replace(/\s+/g, ' ').trim();

  if (clean.length <= 72) {
    return clean;
  }

  const firstSentence = clean.split('. ')[0]?.trim();
  if (firstSentence && firstSentence.length >= 24 && firstSentence.length <= 72) {
    return firstSentence.endsWith('.') ? firstSentence : `${firstSentence}.`;
  }

  return `${clean.slice(0, 69).trim()}...`;
};

export const typingDrills: TypingDrill[] = typingLessons.map((lesson, index) => ({
  id: `drill-${String(index + 1).padStart(2, '0')}`,
  title: `Drill ${index + 1}`,
  titleFa: `تمرین سریع ${index + 1}`,
  text: drillTextFromLesson(lesson.text),
  difficulty: lesson.difficulty,
  timeLimit:
    lesson.difficulty === 'beginner'
      ? 55
      : lesson.difficulty === 'intermediate'
        ? 70
        : 85,
}));
